import type { SectionKey } from "./admin-vendor-form.utils";

export const CLEAN_SECTION_DIRTY: Record<SectionKey, boolean> = {
  basics: false,
  partner: false,
  agreement: false,
  packages: false,
  questions: false,
};

export interface VendorFormDirtySnapshot {
  basics: {
    name: string;
    slug: string;
    description: string;
    capabilities: string[];
    logoUploadId: string | null;
    logoRemoved: boolean;
    hasLocalLogoPreview: boolean;
  };
  agreement: {
    websiteUrl: string;
    defaultDeliveryDays: string;
    slaDaysPerIteration: string;
    termsMarkdown: string;
    isActive: boolean;
    isClientOwned: boolean;
    visibleTenantIds: string[];
    contactEmails: Array<{
      id: string | null;
      email: string;
      isPrimary: boolean;
      receivesRevisionRequested: boolean;
      receivesCommentUpdates: boolean;
    }>;
  };
  packages: Array<{
    id: string | null;
    name: string;
    description: string | null;
    price: number | null;
    currency: string | null;
    cadence: string | null;
    isPopular: boolean;
    isActive: boolean;
  }>;
  questions: Array<{
    id: string | null;
    prompt: string;
    type: string;
    options: string[];
    allowMultiple: boolean;
    isRequired: boolean;
  }>;
}

export interface VendorFormDirtyInput {
  name: string;
  slug: string;
  description: string;
  capabilities: readonly string[];
  logoUploadId: string | null;
  logoRemoved: boolean;
  hasLocalLogoPreview: boolean;
  websiteUrl: string;
  defaultDeliveryDays: string;
  slaDaysPerIteration: string;
  termsMarkdown: string;
  isActive: boolean;
  isClientOwned: boolean;
  visibleTenantIds: readonly string[];
  contactEmails: Array<{
    id?: string;
    email: string;
    isPrimary: boolean;
    receivesRevisionRequested: boolean;
    receivesCommentUpdates: boolean;
  }>;
  packages: Array<{
    id?: string;
    name: string;
    description?: string | null;
    price?: number | string | null;
    currency?: string | null;
    cadence?: string | null;
    isPopular?: boolean;
    isActive?: boolean;
  }>;
  questions: Array<{
    id?: string;
    prompt: string;
    type: string;
    options?: string[] | null;
    allowMultiple?: boolean;
    isRequired?: boolean;
  }>;
}

/** Stable snapshot of the fields that live in each nav-rail section. */
export function captureVendorFormDirtySnapshot(
  input: VendorFormDirtyInput,
): VendorFormDirtySnapshot {
  return {
    basics: {
      name: input.name,
      slug: input.slug,
      description: input.description,
      capabilities: [...input.capabilities].sort(),
      logoUploadId: input.logoUploadId,
      logoRemoved: input.logoRemoved,
      hasLocalLogoPreview: input.hasLocalLogoPreview,
    },
    agreement: {
      websiteUrl: input.websiteUrl,
      defaultDeliveryDays: input.defaultDeliveryDays,
      slaDaysPerIteration: input.slaDaysPerIteration,
      termsMarkdown: input.termsMarkdown,
      isActive: input.isActive,
      isClientOwned: input.isClientOwned,
      visibleTenantIds: [...input.visibleTenantIds],
      contactEmails: input.contactEmails.map((row) => ({
        id: row.id ?? null,
        email: row.email,
        isPrimary: row.isPrimary,
        receivesRevisionRequested: row.receivesRevisionRequested,
        receivesCommentUpdates: row.receivesCommentUpdates,
      })),
    },
    packages: input.packages.map((pkg) => ({
      id: pkg.id ?? null,
      name: pkg.name,
      description: pkg.description ?? null,
      price: normalizePrice(pkg.price),
      currency: pkg.currency ?? null,
      cadence: pkg.cadence ?? null,
      isPopular: pkg.isPopular ?? false,
      isActive: pkg.isActive ?? true,
    })),
    questions: input.questions.map((question) => ({
      id: question.id ?? null,
      prompt: question.prompt,
      type: question.type,
      options: [...(question.options ?? [])],
      allowMultiple: question.allowMultiple ?? true,
      isRequired: question.isRequired ?? true,
    })),
  };
}

/**
 * Per-section dirty flags for edit mode. Partner org connect/unlink persist
 * immediately, so that rail item never shows unsaved.
 */
export function getDirtyVendorFormSections(
  initial: VendorFormDirtySnapshot,
  current: VendorFormDirtySnapshot,
): Record<SectionKey, boolean> {
  return {
    basics: !sameJson(initial.basics, current.basics),
    partner: false,
    agreement: !sameJson(initial.agreement, current.agreement),
    packages: !sameJson(initial.packages, current.packages),
    questions: !sameJson(initial.questions, current.questions),
  };
}

function normalizePrice(price: number | string | null | undefined): number | null {
  if (price === null || price === undefined || price === "") return null;
  const n = typeof price === "number" ? price : Number(price);
  return Number.isFinite(n) ? n : null;
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}
